import React, { useState, useEffect, useCallback } from 'react';
import { paymentAPI, getPublicReceiptUrl } from '../services/api';
import toast from 'react-hot-toast';
import { Image, Printer, X, CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

// Audit log + manual review queue for broadband self-payment screenshot uploads.
// 'applied' rows were already posted automatically at upload time (screenshotPaymentService)
// — only Delete applies to those (removes the screenshot, never the payment). 'duplicate'/'failed_ocr' rows were NOT posted (OCR
// couldn't read the image confidently, or thought it saw a repeat UTR); staff can open the
// screenshot image here to eyeball the real amount/reference number and Accept it manually,
// or Reject it if it isn't a valid payment for this customer.
const STATUS_META = {
  applied:     { label: 'Applied',   badge: 'badge-success' },
  duplicate:   { label: 'Duplicate', badge: 'badge-warning' },
  failed_ocr:  { label: 'Unreadable', badge: 'badge-danger' },
  rejected:    { label: 'Rejected',  badge: 'badge-muted' },
};

const CHIPS = [
  { key: '', label: 'All' },
  { key: 'applied', label: 'Applied' },
  { key: 'duplicate', label: 'Duplicate' },
  { key: 'failed_ocr', label: 'Unreadable' },
  { key: 'rejected', label: 'Rejected' },
];

// Thumbnail for a screenshot — fetched as an authenticated blob (no public URL exists for
// these) and cached as an object URL for the life of this row. Click opens the full image.
function ScreenshotThumb({ id, onOpen }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    paymentAPI.getScreenshotBlob(id).then(({ data }) => {
      if (cancelled) return;
      url = URL.createObjectURL(data);
      setSrc(url);
    }).catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [id]);

  return (
    <button
      type="button"
      onClick={() => src && onOpen(src)}
      style={{ padding: 0, border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 4, background: src ? `url(${src}) center/cover` : '#f3f4f6', cursor: src ? 'pointer' : 'default', width: 40, height: 40, flexShrink: 0 }}
      title="View screenshot"
    />
  );
}

export default function PaymentScreenshotsPage() {
  useDarkReady();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [viewerSrc, setViewerSrc] = useState(null);
  const [acceptRow, setAcceptRow] = useState(null);
  const [acceptForm, setAcceptForm] = useState({ amount: '', transactionId: '' });
  const [accepting, setAccepting] = useState(false);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await paymentAPI.getScreenshots({ page, limit: 20, status: status || undefined });
      setRows(data.data || []);
      setPagination(data.pagination || {});
    } catch (err) {
      toast.error('Failed to load payment screenshots');
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => { load(); }, [load]);

  const openAccept = (row) => {
    setAcceptRow(row);
    setAcceptForm({ amount: row.extractedAmount || '', transactionId: row.extractedUtr || '' });
  };

  const handleAccept = async () => {
    if (!acceptForm.amount || Number(acceptForm.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setAccepting(true);
    try {
      const { data } = await paymentAPI.acceptScreenshot(acceptRow._id, acceptForm);
      toast.success(data.data?.receiptNumber ? `Payment recorded — receipt ${data.data.receiptNumber}` : 'Payment recorded');
      setAcceptRow(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setAccepting(false);
    }
  };

  const openReject = (row) => {
    setRejectRow(row);
    setRejectReason('');
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await paymentAPI.rejectScreenshot(rejectRow._id, { reason: rejectReason });
      toast.success('Screenshot rejected');
      setRejectRow(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject screenshot');
    } finally {
      setRejecting(false);
    }
  };

  const handleDelete = async (row) => {
    const msg = row.status === 'applied'
      ? 'Delete this screenshot? The payment it recorded stays — delete that from Payments if needed.'
      : 'Delete this screenshot?';
    if (!window.confirm(msg)) return;
    try {
      await paymentAPI.deleteScreenshot(row._id);
      toast.success('Screenshot deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete screenshot');
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 4 }}>Payment Screenshots</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 18 }}>
        Broadband self-payments auto-recorded from customer-uploaded UPI screenshots. Open a screenshot to verify it, then Accept if the OCR missed it.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {CHIPS.map(c => (
          <button key={c.key} onClick={() => { setStatus(c.key); setPage(1); }}
            className={`page-btn${status === c.key ? ' active' : ''}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : rows.length === 0 ? (
        <div className="card empty-state" style={{ padding: 48 }}>
          <Image size={32} />
          <h3>No screenshot uploads yet</h3>
          <p>They'll show up here as broadband customers pay via the portal QR code.</p>
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Screenshot</th>
                <th>Customer</th>
                <th>Uploaded</th>
                <th>Amount</th>
                <th>Reference No.</th>
                <th>Status</th>
                <th>Receipt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const meta = STATUS_META[r.status] || { label: r.status, badge: 'badge-info' };
                return (
                  <tr key={r._id}>
                    <td><ScreenshotThumb id={r._id} onOpen={setViewerSrc} /></td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.customer?.name || '—'}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{r.customer?.customerId}</div>
                    </td>
                    <td style={{ fontSize: '.82rem' }}>{new Date(r.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                    <td>{r.extractedAmount ? `₹${new Intl.NumberFormat('en-IN').format(r.extractedAmount)}` : '—'}</td>
                    <td className="font-mono" style={{ fontSize: '.8rem' }}>{r.extractedUtr || '—'}</td>
                    <td>
                      <span className={`badge ${meta.badge}`}>{meta.label}</span>
                      {r.failureReason && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{r.failureReason}</div>}
                    </td>
                    <td>
                      {r.payment?.receiptNumber ? (
                        <a href={getPublicReceiptUrl(r.payment)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                          <Printer size={12} /> {r.payment.receiptNumber}
                        </a>
                      ) : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {r.status !== 'applied' && (
                          <button className="btn btn-primary btn-sm" onClick={() => openAccept(r)}>
                            <CheckCircle2 size={12} /> Accept
                          </button>
                        )}
                        {r.status !== 'applied' && r.status !== 'rejected' && (
                          <button className="btn btn-secondary btn-sm" onClick={() => openReject(r)}>
                            <XCircle size={12} /> Reject
                          </button>
                        )}
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r)} title="Delete screenshot">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pagination.pages > 1 && (
        <div className="pagination">
          <span>Page {pagination.page} of {pagination.pages} · {pagination.total} total</span>
          <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
          <button className="page-btn" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}

      {viewerSrc && (
        <div className="modal-overlay" onClick={() => setViewerSrc(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" style={{ position: 'absolute', top: -10, right: -10, background: 'var(--cb-ffffff)', borderRadius: '50%' }} onClick={() => setViewerSrc(null)}>
              <X size={16} />
            </button>
            <img src={viewerSrc} alt="Payment screenshot" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 6, display: 'block' }} />
          </div>
        </div>
      )}

      {acceptRow && (
        <div className="modal-overlay" onClick={() => !accepting && setAcceptRow(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Accept Payment</h3>
              <button className="modal-close" onClick={() => setAcceptRow(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                {acceptRow.customer?.name} — verify against the screenshot before confirming.
              </div>
              <ScreenshotThumb id={acceptRow._id} onOpen={setViewerSrc} />
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: '.78rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Amount (₹)</label>
                <input className="form-control" type="number" min="0" step="0.01" value={acceptForm.amount}
                  onChange={e => setAcceptForm(f => ({ ...f, amount: e.target.value }))} />
              </div>
              <div style={{ marginTop: 10 }}>
                <label style={{ fontSize: '.78rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Reference No. (optional)</label>
                <input className="form-control" value={acceptForm.transactionId}
                  onChange={e => setAcceptForm(f => ({ ...f, transactionId: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setAcceptRow(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={accepting} onClick={handleAccept}>
                {accepting ? 'Recording…' : 'Accept & Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectRow && (
        <div className="modal-overlay" onClick={() => !rejecting && setRejectRow(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Screenshot</h3>
              <button className="modal-close" onClick={() => setRejectRow(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                {rejectRow.customer?.name} — no payment will be recorded. This can't be undone.
              </div>
              <ScreenshotThumb id={rejectRow._id} onOpen={setViewerSrc} />
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: '.78rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Reason (optional)</label>
                <input className="form-control" value={rejectReason} placeholder="e.g. not a payment for this account"
                  onChange={e => setRejectReason(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setRejectRow(null)}>Cancel</button>
              <button className="btn btn-danger" disabled={rejecting} onClick={handleReject}>
                {rejecting ? 'Rejecting…' : 'Reject Screenshot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
